import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Image, Text, keyframes } from '@chakra-ui/react';
import { Add, ChevronRight, ContentCopy, ThumbUpOutlined, ThumbDownOutlined, Check } from '@mui/icons-material';

import { useCartContext } from '../contexts/CartContext';
import { useAgent } from './AgentProvider';
import { formatPrice } from '../components/product/productUtils';

// Message blocks for the Raano AI panel (design-system.md §9.2)

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none }`;
const pulse = keyframes`0%, 100% { transform: scale(0.8); opacity: .5 } 50% { transform: scale(1.15); opacity: 1 }`;

// **bold** and *italic* only; everything else is plain text
const Markdown = ({ text }) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
    return parts.map((p, i) => p.startsWith('**') ? <Box as='strong' key={i} fontWeight={600} >{p.slice(2, -2)}</Box>
        : p.startsWith('*') ? <Box as='em' key={i} >{p.slice(1, -1)}</Box> : <React.Fragment key={i} >{p}</React.Fragment>);
};

export const UserBubble = ({ text, t }) => (
    <Box display='flex' justifyContent='flex-end' animation={`${fadeIn} .2s ease-out`} >
        <Box maxWidth='80%' bg={t.bubble} color={t.text} px='16px' py='12px' borderRadius='20px' fontSize='16px' lineHeight='22px' >{text}</Box>
    </Box>
);

const ProductCard = ({ product, t, onAdd }) => {
    const navigate = useNavigate();
    const { close } = useAgent();
    return (
        <Box display='flex' gap='14px' p='12px' bg={t.card} border='1px solid' borderColor={t.cardBorder} borderRadius='24px' >
            <Box width={{ base: '104px', md: '136px' }} flexShrink={0} borderRadius='20px' overflow='hidden' bg='rgba(0,0,0,0.03)' sx={{ aspectRatio: '1 / 1' }} >
                <Image src={product.imageUrl} alt={product.name} width='100%' height='100%' objectFit='cover' objectPosition='top' />
            </Box>
            <Box flex={1} minWidth={0} display='flex' flexDirection='column' >
                <Text as='button' textAlign='left' fontSize='15px' lineHeight='20px' fontWeight={700} textDecoration='underline' noOfLines={1} onClick={() => { close(); navigate(`/product/${product._id}`); }} >{product.name}</Text>
                <Text fontSize='15px' lineHeight='20px' mt='4px' >{formatPrice(product.price, { space: false })}.00</Text>
                <Box as='button' mt='auto' height='40px' borderRadius='16px' bg={t.cardBtnBg} color={t.cardBtnText} border='1px solid' borderColor={t.cardBtnBg} fontSize='14px' fontWeight={700} display='flex' alignItems='center' justifyContent='center' gap='6px' transition='opacity .125s' _hover={{ opacity: 0.85 }} onClick={() => onAdd(product)} >
                    <Add sx={{ fontSize: 18 }} />Add to cart
                </Box>
            </Box>
        </Box>
    );
};

const Carousel = ({ title, products, t, onAdd }) => {
    const navigate = useNavigate();
    const { close } = useAgent();
    return (
        <Box>
            {title && <Text fontSize='14px' fontWeight={600} mb='10px' >{title}</Text>}
            <Box display='flex' gap='10px' overflowX='auto' pb='6px' mx='-20px' px='20px' sx={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }} >
                {products.map(p => (
                    <Box key={p._id} width='156px' flexShrink={0} sx={{ scrollSnapAlign: 'start' }} >
                        <Box position='relative' borderRadius='16px' overflow='hidden' bg='rgba(0,0,0,0.03)' sx={{ aspectRatio: '4 / 5' }} >
                            <Image src={p.imageUrl} alt={p.name} width='100%' height='100%' objectFit='cover' objectPosition='top' cursor='pointer' onClick={() => { close(); navigate(`/product/${p._id}`); }} />
                            <Box as='button' aria-label={`Add ${p.name} to cart`} position='absolute' right='8px' bottom='8px' width='32px' height='32px' borderRadius='full' bg={t.cardBtnBg} color={t.cardBtnText} display='flex' alignItems='center' justifyContent='center' onClick={() => onAdd(p)} ><Add sx={{ fontSize: 18 }} /></Box>
                        </Box>
                        <Text fontSize='13px' fontWeight={700} textDecoration='underline' mt='6px' noOfLines={1} >{p.name}</Text>
                        <Text fontSize='13px' >{formatPrice(p.price, { space: false })}</Text>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Size picker that appears inside the chat after "Add to cart"
const VariantPicker = ({ product, t }) => {
    const { addItem } = useCartContext();
    const { setCartDot } = useAgent();
    const [size, setSize] = useState(product.sizes?.length === 1 ? product.sizes[0] : '');
    const [done, setDone] = useState(false);
    return (
        <Box p='14px' bg={t.card} border='1px solid' borderColor={t.cardBorder} borderRadius='24px' >
            <Box display='flex' gap='12px' alignItems='center' >
                <Image src={product.imageUrl} alt='' width='48px' height='48px' borderRadius='12px' objectFit='cover' objectPosition='top' />
                <Box minWidth={0} >
                    <Text fontSize='14px' fontWeight={700} noOfLines={1} >{product.name}</Text>
                    <Text fontSize='14px' >{formatPrice(product.price, { space: false })}</Text>
                </Box>
            </Box>
            <Text fontSize='13px' fontWeight={600} mt='12px' mb='6px' >Size</Text>
            <Box display='flex' flexWrap='wrap' gap='6px' >
                {(product.sizes || []).map(s => (
                    <Box key={s} as='button' minWidth='44px' height='34px' px='10px' borderRadius='full' border='1px solid' fontSize='13px'
                        borderColor={size === s ? t.cardBtnBg : t.chipBorder} bg={size === s ? t.cardBtnBg : 'transparent'} color={size === s ? t.cardBtnText : t.text} onClick={() => setSize(s)} disabled={done} >{s}</Box>
                ))}
            </Box>
            <Text fontSize='13px' fontWeight={600} mt='12px' mb='6px' >Color</Text>
            <Box display='inline-flex' px='12px' height='30px' alignItems='center' borderRadius='full' border='1px solid' borderColor={t.chipBorder} fontSize='13px' textTransform='capitalize' >{product.color}</Box>
            <Box as='button' width='100%' mt='14px' height='40px' borderRadius='16px' bg={t.cardBtnBg} color={t.cardBtnText} fontSize='14px' fontWeight={700} opacity={!size ? 0.4 : 1} disabled={!size || done}
                display='flex' alignItems='center' justifyContent='center' gap='6px'
                onClick={() => { addItem(product, size, 1); setDone(true); setCartDot(true); }} >
                {done ? <><Check sx={{ fontSize: 18 }} />Added to cart</> : 'Confirm'}
            </Box>
        </Box>
    );
};

export const Chips = ({ items, t }) => {
    const { sendText, status } = useAgent();
    return (
        <Box display='flex' flexWrap='wrap' gap='8px' >
            {items.map(c => (
                <Box key={c} as='button' height='40px' px='20px' borderRadius='24px' border='1px solid' borderColor={t.chipBorder} bg={t.chipBg} color={t.chipText} fontSize='16px' lineHeight='22px'
                    transition='background .125s' _hover={{ bg: t.hover }} disabled={status === 'thinking'} onClick={() => sendText(c)} >{c}</Box>
            ))}
        </Box>
    );
};

export const Thinking = ({ t }) => {
    const shimmer = keyframes`from { background-position: 100% 0 } to { background-position: -100% 0 }`;
    return (
        <Box display='flex' alignItems='center' gap='10px' animation={`${fadeIn} .2s ease-out`} >
            <Box width='10px' height='10px' borderRadius='full' bg={t.text} animation={`${pulse} 1.2s ease-in-out infinite`} />
            <Text fontSize='15px' color='transparent' bgClip='text' bgSize='200% 100%' bgImage={`linear-gradient(90deg, ${t.muted} 0%, ${t.text} 50%, ${t.muted} 100%)`} animation={`${shimmer} 1.6s linear infinite`} >
                Thinking this through, just a moment…
            </Text>
        </Box>
    );
};

// One assistant turn: renders each block, then copy / 👍 / 👎
export const AssistantTurn = ({ blocks, t, onAdd, last }) => {
    const navigate = useNavigate();
    const { catalog, close } = useAgent();
    const byId = (pid) => catalog.products.find(p => p._id === pid);
    const [copied, setCopied] = useState(false);
    const [vote, setVote] = useState(null);
    const text = blocks.filter(b => b.type === 'text').map(b => b.markdown.replace(/\*/g, '')).join('\n');

    return (
        <Box display='flex' flexDirection='column' gap='14px' animation={`${fadeIn} .2s ease-out`} >
            {blocks.map((b, i) => {
                if (b.type === 'text') return <Text key={i} fontSize='16px' lineHeight='22px' ><Markdown text={b.markdown} /></Text>;
                if (b.type === 'products') return b.items.map(byId).filter(Boolean).map(p => <ProductCard key={p._id + i} product={p} t={t} onAdd={onAdd} />);
                if (b.type === 'carousel') return <Carousel key={i} title={b.title} products={b.items.map(byId).filter(Boolean)} t={t} onAdd={onAdd} />;
                if (b.type === 'variantPicker') { const p = byId(b.productId); return p ? <VariantPicker key={i} product={p} t={t} /> : null; }
                if (b.type === 'showMore') return (
                    <Box key={i} as='button' display='flex' alignItems='center' gap='12px' p='10px' border='1px solid' borderColor={t.cardBorder} borderRadius='16px' textAlign='left' onClick={() => { close(); navigate('/search', { state: b.state }); }} >
                        <Box display='flex' >{b.state?.categoryId && catalog.products.filter(p => p.category === b.state.categoryId).slice(0, 2).map(p => <Image key={p._id} src={p.imageUrl} alt='' width='28px' height='36px' objectFit='cover' borderRadius='4px' mr='-8px' border='2px solid' borderColor={t.card} />)}</Box>
                        <Text flex={1} fontSize='14px' fontWeight={700} pl='6px' >{b.label}</Text>
                        <ChevronRight sx={{ fontSize: 20 }} />
                    </Box>
                );
                if (b.type === 'chips' && last) return <Chips key={i} items={b.items} t={t} />;
                return null;
            })}
            {text &&
                <Box display='flex' gap='4px' color={t.muted} >
                    {[[copied ? Check : ContentCopy, 'Copy', () => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }],
                    [ThumbUpOutlined, 'Good response', () => setVote('up'), vote === 'up'],
                    [ThumbDownOutlined, 'Bad response', () => setVote('down'), vote === 'down']].map(([Icon, label, fn, on]) => (
                        <Box key={label} as='button' aria-label={label} width='28px' height='28px' borderRadius='6px' display='flex' alignItems='center' justifyContent='center' color={on ? t.text : undefined} _hover={{ bg: t.hover, color: t.text }} onClick={fn} >
                            <Icon sx={{ fontSize: 17 }} />
                        </Box>
                    ))}
                </Box>}
        </Box>
    );
};
